import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { PaintBrush } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  INode,
  ITypeNode,
  useFormBuilder,
} from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';
import { InputFormData } from '../../schemas/input.schema';
import { inputFormToNode, inputNodeToForm } from '../../utils/nodeDataTransformers';
import { CustomizationForm } from '../FormCustomization/CustomizationForm';

interface InputSettingsProps {
  node: INode;
}

interface DataInputNode {
  id: string;
  label: string;
  type: string;
}

export function InputSettings({ node }: InputSettingsProps) {
  const { dispatch, state } = useFormBuilder();
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const formsMethods = useForm<InputFormData>({
    defaultValues: inputNodeToForm(node),
  });

  useEffect(() => {
    formsMethods.reset(inputNodeToForm(node));
  }, [node.id, node.data, formsMethods]);

  const handleNodeSelect = (node: DataInputNode) => {
    setExpandedNodeId(expandedNodeId === node.id ? null : node.id);
    formsMethods.setValue('varDataInputId', node.id);
  };

  useEffect(() => {
    const subscription = formsMethods.watch((data) => {
      if (data && Object.keys(data).length > 0) {
        const updatedData = inputFormToNode(data as InputFormData, node);
        dispatch({
          type: ActionTypes.UPDATE_NODES_DATA,
          payload: {
            id: node.id,
            updates: { data: updatedData },
          },
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [formsMethods.watch, node.id, dispatch]);

  const formValues = formsMethods.watch();

  const selectedNodeData = state.varInputNodes.find(
    node => node.id === formValues.varDataInputId,
  );

  const fileOptions = ['PDF', 'TXT', 'DOCX', 'PNG', 'JPEG', 'JPG', 'PPTX', 'XLSX', 'CSV', 'XLS'];

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '16px 16px 0 16px',
        }}
      >
        <PaintBrush size={16} color="#101828" />
        <Typography
          style={{ fontSize: '14px', fontWeight: '500', color: '#101828' }}
        >
          Input Settings
        </Typography>
      </Box>

      <FormProvider {...formsMethods}>
        <form style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              padding: '0px 16px 0 16px',
            }}
          >
            <Box
              onClick={() => setActiveTab(0)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 0 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 0 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 0 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Content
            </Box>

            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 1 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 1 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 1 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Design
            </Box>
          </Box>

          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
          />

          {activeTab === 0 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Controller
                name="title"
                control={formsMethods.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Title"
                    placeholder="Enter title..."
                    fullWidth
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        margin: '0px',
                        fontSize: '14px',
                        height: '38px',
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="subtitle"
                control={formsMethods.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Subtitle"
                    placeholder="Enter subtitle..."
                    fullWidth
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        margin: '0px',
                        fontSize: '14px',
                        height: '38px',
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="placeholder"
                control={formsMethods.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Placeholder"
                    placeholder="Enter placeholder..."
                    fullWidth
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        margin: '0px',
                        fontSize: '14px',
                        height: '38px',
                      },
                    }}
                  />
                )}
              />

              <Typography
                style={{
                  fontSize: '12px',
                  fontWeight: '400',
                  color: '#475467',
                }}
              >
                Please select which "Data input" to use for this list field
              </Typography>

              {selectedNodeData && (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                >
                  <Typography
                    style={{
                      fontSize: '12px',
                      fontWeight: '400',
                      color: '#475467',
                    }}
                  >
                    Node selected:
                  </Typography>

                  <div
                    style={{
                      backgroundColor: '#F9FAFB',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #E4E7EC',
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{ fontWeight: '500', marginBottom: '4px' }}
                    >
                      Selected: {selectedNodeData.label}
                    </Typography>
                    <Typography variant="caption" style={{ color: '#475467' }}>
                      Type: {formValues?.fieldType} | Required:{' '}
                      {formValues?.requiredField ? 'Yes' : 'No'}
                    </Typography>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                <Typography
                  style={{
                    fontSize: '12px',
                    fontWeight: '400',
                    color: '#475467',
                  }}
                >
                  Select data input
                </Typography>

                {state.varInputNodes.map(node => (
                  <div
                    key={node.id}
                    style={{
                      border: '1px solid #E4E7EC',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <div
                      onClick={() => handleNodeSelect(node)}
                      style={{
                        padding: '12px',
                        cursor: 'pointer',
                        backgroundColor:
                          expandedNodeId === node.id ? '#F0F4FF' : 'transparent',
                        borderBottom:
                          expandedNodeId === node.id ? '1px solid #E4E7EC' : 'none',
                      }}
                      onMouseEnter={e => {
                        if (expandedNodeId !== node.id) {
                          e.currentTarget.style.backgroundColor = '#F9FAFB';
                          e.currentTarget.style.borderColor = '#3650DE';
                        }
                      }}
                      onMouseLeave={e => {
                        if (expandedNodeId !== node.id) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = '#E4E7EC';
                        }
                      }}
                    >
                      <Typography variant="subtitle2" style={{ fontWeight: '500' }}>
                        {node.label}
                      </Typography>
                    </div>

                    {expandedNodeId === node.id && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '16px',
                          backgroundColor: '#FAFBFC',
                          gap: '8px',
                        }}
                      >
                        <FormControl component="fieldset">
                          <FormLabel
                            component="legend"
                            style={{ fontSize: '12px', fontWeight: '400' }}
                          >
                            Field Type
                          </FormLabel>
                          <Controller
                            name="fieldType"
                            control={formsMethods.control}
                            render={({ field }) => (
                              <RadioGroup
                                value={field.value}
                                onChange={e => field.onChange(e.target.value)}
                                style={{ flexDirection: 'row' }}
                              >
                                <FormControlLabel
                                  value="data"
                                  control={<Radio size="small" />}
                                  label="Data only"
                                />
                                <FormControlLabel
                                  value="upload"
                                  control={<Radio size="small" />}
                                  label="Upload only"
                                />
                                <FormControlLabel
                                  value="both"
                                  control={<Radio size="small" />}
                                  label="Both"
                                />
                              </RadioGroup>
                            )}
                          />
                        </FormControl>

                        <FormControl component="fieldset">
                          <FormLabel
                            component="legend"
                            style={{ fontSize: '12px', fontWeight: '4500' }}
                          >
                            Required Field
                          </FormLabel>
                          <Controller
                            name="requiredField"
                            control={formsMethods.control}
                            render={({ field }) => (
                              <RadioGroup
                                value={field.value ? 'true' : 'false'}
                                onChange={e =>
                                  field.onChange(e.target.value === 'true')
                                }
                                style={{ flexDirection: 'row' }}
                              >
                                <FormControlLabel
                                  value="true"
                                  control={<Radio size="small" />}
                                  label="Required"
                                />
                                <FormControlLabel
                                  value="false"
                                  control={<Radio size="small" />}
                                  label="Optional"
                                />
                              </RadioGroup>
                            )}
                          />
                        </FormControl>

                        {(formValues?.fieldType === 'data' ||
                          formValues?.fieldType === 'both') && (
                          <FormControl component="fieldset">
                            <FormLabel
                              component="legend"
                              style={{ fontSize: '12px', fontWeight: '500' }}
                            >
                              Line Count
                            </FormLabel>
                            <Controller
                              name="minRows"
                              control={formsMethods.control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  type="number"
                                  size="small"
                                  fullWidth
                                  variant="outlined"
                                  sx={{
                                    '& .MuiInputBase-root': {
                                      fontSize: '14px',
                                    },
                                  }}
                                  inputProps={{ min: 1, max: 20 }}
                                  onChange={e =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              )}
                            />
                          </FormControl>
                        )}

                        {(formValues?.fieldType === 'upload' ||
                          formValues?.fieldType === 'both') && (
                          <FormControl component="fieldset" fullWidth>
                            <FormLabel
                              component="legend"
                              style={{ fontSize: '12px', fontWeight: '500' }}
                            >
                              Allowed File Types
                            </FormLabel>
                            <Controller
                              name="allowedFiles"
                              control={formsMethods.control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  multiple
                                  size="small"
                                  fullWidth
                                  input={<OutlinedInput />}
                                  sx={{
                                    '& .MuiInputBase-root': {
                                      height: '32px',
                                      borderRadius: '4px',
                                      border: '1px solid #EAECF0',
                                      backgroundColor: '#F9FAFB',
                                      color: '#475467',
                                      margin: '0px',
                                      fontSize: '14px',
                                    },
                                  }}
                                  renderValue={selected => (
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                      }}
                                    >
                                      {selected.map(value => (
                                        <Chip
                                          key={value}
                                          label={value}
                                          size="small"
                                          style={{ fontSize: '14px' }}
                                        />
                                      ))}
                                    </div>
                                  )}
                                >
                                  {fileOptions.map(option => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                          </FormControl>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <CustomizationForm
                control={formsMethods.control}
                type={ITypeNode.INPUT}
              />
            </Box>
          )}
        </form>
      </FormProvider>
    </>
  );
}
